import { createContext, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { usePageNavigation } from "../components/dataTable/hooks/usePageNavigation";
import { QueryRequest, QueryResult, SortCriterion } from "../types/responses";
import { UserDataContext } from "./userDataContext";

export const DataTableServerSideContext = createContext({});

// Builds a user-facing warning when the server reports sort/filter fields it
// could not apply, or null when there is nothing to warn about.
export const buildIgnoredFieldsWarning = (
	result?: Pick<QueryResult, "ignoredSortFields" | "ignoredFilterFields">,
): string | null => {
	const sorts = result?.ignoredSortFields ?? [];
	const filters = result?.ignoredFilterFields ?? [];
	if (sorts.length === 0 && filters.length === 0) return null;

	const parts: string[] = [];
	if (sorts.length) parts.push(`sorting by ${sorts.join(", ")}`);
	if (filters.length) parts.push(`filtering by ${filters.join(", ")}`);
	return `This view doesn't support ${parts.join(" or ")}.`;
};

export const extractDefaultFilters = (
	columnSettings: any,
): Record<string, any> => {
	const defaultFilters: Record<string, any> = {};

	if (columnSettings?.columns) {
		columnSettings.columns.forEach((column: any) => {
			if (column.defaultFilterValue !== undefined) {
				const key = column.id || column.accessor;
				if (key) {
					defaultFilters[key] = column.defaultFilterValue;
				}
			}
		});
	}

	return defaultFilters;
};

export const DataTableServerSideProvider = ({
	children,
	initialIsErrorState = false,
	apiFunction,
	dataTableColumnSettings,
	dataTableEditorSettings,
	triggerRefresh = null,
	isLoading = false,
	queryKey = "queryKey",
	refreshKey = queryKey,
	isEnabled = true,
	orgScoped = true,
}: {
	children: any;
	initialIsErrorState?: boolean | null;
	apiFunction: (
		args: QueryRequest & { orgId?: string },
	) => Promise<QueryResult>;
	dataTableColumnSettings: any;
	dataTableEditorSettings?: any;
	triggerRefresh?: boolean | null;
	isLoading?: boolean | null;
	queryKey?: string | string[];
	refreshKey?: string | string[];
	isEnabled?: boolean;
	// Defaults true: the selected org id is passed to apiFunction, so org-scoped
	// endpoints don't each need a wrapper to inject it. Set false for global /
	// sysadmin grids that are not scoped to the selected org.
	orgScoped?: boolean;
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalAction, setOrgModalAction] = useState("");
	const [initialModalData, setInitialModalData] = useState([{}]);
	const [isSelect, setIsSelect] = useState(false);

	const queryKeys = Array.isArray(queryKey) ? queryKey : [queryKey];
	const refreshKeys = Array.isArray(refreshKey) ? refreshKey : [refreshKey];

	const [filterCriteria, setFilterCriteria] = useState<
		Record<string, object>
	>(extractDefaultFilters(dataTableColumnSettings));
	const [sortCriteria, setSortCriteria] = useState([
		{ field: dataTableColumnSettings.columns[0].id, isAscending: true },
	] as SortCriterion[]);

	const pagination = usePageNavigation();
	const { pageNo, pageSize, setFromQueryData } = pagination;

	const { selectedOrgId } = useContext(UserDataContext);
	const {
		isLoading: isTableLoading,
		isFetching,
		error,
		data,
	} = useQuery(
		[
			...queryKeys,
			"selectedOrgId",
			selectedOrgId,
			"filters",
			filterCriteria,
			"pageNo",
			pageNo,
			"pageSize",
			pageSize,
			"sortCriteria",
			sortCriteria,
		],
		() =>
			apiFunction({
				...(orgScoped ? { orgId: selectedOrgId } : {}),
				filterCriteria,
				pageNo,
				pageSize,
				sortCriteria,
			}),
		{
			enabled: isEnabled,
			keepPreviousData: true,
		},
	);

	const queryData =
		data ||
		({
			tableData: [],
			paginationMetadata: {
				pageNo: 1,
				pageSize: 0,
				totalPages: 1,
				totalAvailable: 0,
				fetchedCount: 0,
			},
		} as QueryResult);

	useEffect(() => {
		if (queryData && isFetching === false) {
			setFromQueryData(queryData);
		}
	}, [queryData, setFromQueryData]);

	// Warn the user when the server ignored a requested sort/filter field.
	useEffect(() => {
		if (!data || isFetching) return;
		const message = buildIgnoredFieldsWarning(data);
		if (message) toast.warning(message, { toastId: message });
	}, [data, isFetching]);

	// Prefer a wrapper-provided (possibly transformed) tableData; otherwise use the
	// backend's raw `results`, so wrappers no longer need to remap results -> tableData.
	const tableData = queryData.tableData ?? queryData.results ?? [];

	let dataError = null;
	if (!Array.isArray(tableData)) {
		dataError = "Bad data";
		console.error(dataError);
	}

	// Tolerate the pre-nesting backend shape (root fetchedCount/totalAvailable) so the
	// count isn't understated to the current page's length when nested fields are absent.
	const fetched =
		queryData?.paginationMetadata?.fetchedCount ??
		queryData?.fetchedCount ??
		tableData.length;
	const totalAvailable =
		queryData?.paginationMetadata?.totalAvailable ??
		queryData?.totalAvailable ??
		tableData.length;

	const queryClient = useQueryClient();

	const refreshTableData = () =>
		queryClient.invalidateQueries({ queryKey: [...refreshKeys] });

	useEffect(() => {
		if (data && !isFetching && !error) {
			const totalPagesCount = queryData.paginationMetadata.totalPages;
			const pagesToPrefetch: number[] = [];

			// Always prefetch first page (if not current)
			if (pageNo !== 1) {
				pagesToPrefetch.push(1);
			}

			// Always prefetch last page (if not current and exists)
			if (pageNo !== totalPagesCount && totalPagesCount > 1) {
				pagesToPrefetch.push(totalPagesCount);
			}

			// Prefetch previous 2 pages
			if (pageNo > 1) {
				pagesToPrefetch.push(pageNo - 1);
			}
			if (pageNo > 2) {
				pagesToPrefetch.push(pageNo - 2);
			}

			// Prefetch next 2 pages
			if (pageNo < totalPagesCount) {
				pagesToPrefetch.push(pageNo + 1);
			}
			if (pageNo < totalPagesCount - 1) {
				pagesToPrefetch.push(pageNo + 2);
			}

			// Remove duplicates and prefetch
			const uniquePages = [...new Set(pagesToPrefetch)];

			uniquePages.forEach((prefetchPageNo) => {
				queryClient.prefetchQuery(
					[
						...queryKeys,
						"selectedOrgId",
						selectedOrgId,
						"filters",
						filterCriteria,
						"pageNo",
						prefetchPageNo,
						"pageSize",
						pageSize,
						"sortCriteria",
						sortCriteria,
					],
					() =>
						apiFunction({
							...(orgScoped ? { orgId: selectedOrgId } : {}),
							filterCriteria,
							pageNo: prefetchPageNo,
							pageSize,
							sortCriteria,
						}),
					{
						staleTime: 5 * 60 * 1000, // Cache for 5 minutes
					},
				);
			});
		}
	}, [
		pageNo,
		pageSize,
		filterCriteria,
		selectedOrgId,
		data,
		isFetching,
		queryClient,
		queryKeys,
		error,
		apiFunction,
		orgScoped,
		queryData.paginationMetadata.totalPages,
	]);

	useEffect(() => {
		if (triggerRefresh !== null) refreshTableData();
	}, [triggerRefresh]);

	// Reset to page 1 when filters change
	// useEffect(() => {
	// 	resetToFirstPage();
	// }, [filterCriteria]);

	return (
		<DataTableServerSideContext.Provider
			value={{
				isModalOpen,
				setIsModalOpen,
				modalAction,
				setOrgModalAction,
				pagination,
				sortCriteria,
				setSortCriteria,
				initialModalData,
				setInitialModalData,
				isLoading: isTableLoading || isLoading,
				isFetching,
				tableData,
				isError: initialIsErrorState || error || dataError,
				dataTableColumnSettings,
				dataTableEditorSettings,
				paginationData: { fetched, totalAvailable },
				refreshTableData,
				filterCriteria,
				setFilterCriteria,
				isSelect,
				setIsSelect,
			}}
		>
			{children}
		</DataTableServerSideContext.Provider>
	);
};
